import { saveAs } from 'file-saver';
import { Popconfirm, message, Space, Popover, Table, Spin } from 'antd';
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  FileAddOutlined,
  FolderAddOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { Printer } from '@phosphor-icons/react';
import { Form, Formik } from 'formik';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactSelect from 'react-select';
import { CircularProgress } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import type { ColumnsType } from 'antd/es/table';

import useTranslation from 'customHooks/translate';
import { useDialogHandler } from 'hooks/useDialogHandler';
import {
  getInvoiceDetails,
  getInvoiceLinesDetails,
  getInvoicePaymentDetails,
  cancelInvoice,
  getInvoiceDownload,
  sendEmail,
  deleteInvoicePayment,
  makeAkt,
  updateInvoice,
} from 'services/invoices';
import { getManagers } from 'services/managers';
import { getJobs } from 'services/jobs';
import { getAccountData } from 'services/accounts';
import { setCulture } from 'services/others';
import AddNewInvoiceForm from 'pages/Dialogs/AddNewInvoiceForm';
import { INCOMES_PAGE_URL, PAGE_URL_KEY, REPORTS_PAGE_URL } from 'consts';
import { InvoiceDetailsDataType, InvoiceDetailsHeaderCustomError } from 'types';
import {
  ACCOUNT_INFO,
  INVOICE_DETAILS,
  INVOICE_JOBS,
  INVOICE_LINES_DETAILS,
  INVOICE_PAYMENT_DETAILS,
  MANAGERS,
  REPORTS_EXPENSES_SUMMARY,
} from 'events/types';

import PaymentModal from '../../../Dialogs/PaymentModal';
import StatusTag from '../../StatusTag';
import InvoiceDetailsInfo from './InvoiceDetailsInfo';

interface PaymentData {
  paymentMethodDesc: string;
  paymentMethod: string;
  paymentAmount: number;
  taxAmount: number;
  paymentReceivedDate: string;
  paymentReceiveId: number;
  key: number;
  currency: string;
}

const defaultValues: InvoiceDetailsDataType = {
  managerId: null,
  invoiceJobId: null,
  contractNumber: '',
  contractDate: '',
};

export const InvoiceDetailsHeader = () => {
  const queryClient = useQueryClient();
  const translation = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const navigate = useNavigate();
  const [invoiceDialogState, openInvoiceDialog, closeInvoiceDialog] =
    useDialogHandler();
  const [isMobile, setIsMobile] = useState(false);
  const { id: invoiceId, partnerId } = useParams();
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language')
  );
  const [formKey, setFormKey] = useState(0);
  const [isLanguageChanging, setIsLanguageChanging] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [paymentTableData, setPaymentTableData] = useState<PaymentData[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const [formData, setFormData] =
    useState<InvoiceDetailsDataType>(defaultValues);
  const [loading, setLoading] = useState(false);
  const previousPageUrl = localStorage.getItem(PAGE_URL_KEY);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { data: invoiceDetails, refetch: refetchInvoiceDetails } = useQuery({
    queryKey: [INVOICE_DETAILS, invoiceId],
    queryFn: () => getInvoiceDetails(+invoiceId!),
    select: (response) => response.data.data,
    enabled: !isLanguageChanging,
  });

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
    refetchOnWindowFocus: true,
    staleTime: 0,
    cacheTime: 0,
  });
  const accountData = accountInfoQuery.data;

  const { data: invoiceLinesDetails, refetch: refetchInvoiceLineDetails } =
    useQuery({
      queryKey: [INVOICE_LINES_DETAILS, invoiceId],
      queryFn: () => getInvoiceLinesDetails(+invoiceId!),
      select: (response) => response.data.data,
      enabled: !isLanguageChanging,
    });

  const { data: managers } = useQuery({
    queryKey: [MANAGERS],
    queryFn: getManagers,
    select: (response) => {
      return response.data.data?.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const { data: jobNumbers } = useQuery({
    queryKey: [INVOICE_JOBS, invoiceId],
    queryFn: () => getJobs(invoiceId),
    select: (response) => {
      return response.data.data?.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const fetchPaymentData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getInvoicePaymentDetails(
          +invoiceId!,
          pageSize,
          currentPage
        );

        if (response?.data?.data?.length) {
          const newData = response.data.data.map((item: any) => ({
            ...item,
            key: item.paymentReceiveId,
          }));

          if (isNewRequest) {
            setPaymentTableData(newData);
          } else {
            setPaymentTableData((prev) => [...prev, ...newData]);
          }

          const newTotalItems =
            (isNewRequest ? 0 : paymentTableData.length) + newData.length;
          const hasMoreItems = newTotalItems < response.data.totalRecord;
          setHasMore(hasMoreItems);

          return response.data;
        }
        return null;
      } catch (error: any) {
        message.error('Error loading payment data');
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [invoiceId, pageSize, paymentTableData.length]
  );

  const resetPaymentData = () => {
    setPaymentTableData([]);
    setPageIndex(0);
    setHasMore(true);
  };

  const { isLoading: isPaymentLoading, refetch: refetchPaymentDetails } =
    useQuery(
      [INVOICE_PAYMENT_DETAILS, invoiceId],
      () => fetchPaymentData(0, true),
      {
        enabled: !isLanguageChanging && !!invoiceId,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        staleTime: 0,
        cacheTime: 60000,
      }
    );

  const handleScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMore ||
      isPaymentLoading
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchPaymentData(nextPage);
    }
  }, [pageIndex, hasMore, isPaymentLoading, fetchPaymentData]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    const checkLanguageAndUpdateBackend = async () => {
      const storedLanguage = localStorage.getItem('language');
      if (storedLanguage && storedLanguage !== currentLanguage) {
        setIsLanguageChanging(true);

        try {
          await setCulture(storedLanguage);
          setCurrentLanguage(storedLanguage);
          await new Promise((resolve) => setTimeout(resolve, 300));
          await refetchInvoiceDetails();
          resetPaymentData();
          await refetchPaymentDetails();
          await refetchInvoiceLineDetails();
          setFormKey((prev) => prev + 1);
        } catch {
        } finally {
          setIsLanguageChanging(false);
        }
      }
    };

    checkLanguageAndUpdateBackend();

    const handleStorageChange = () => {
      checkLanguageAndUpdateBackend();
    };

    window.addEventListener('storage', handleStorageChange);
    const intervalId = setInterval(checkLanguageAndUpdateBackend, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [
    currentLanguage,
    refetchInvoiceDetails,
    refetchPaymentDetails,
    refetchInvoiceLineDetails,
  ]);

  const invoice = invoiceDetails;

  const handleCancelInvoice = async (invoiceId: any) => {
    try {
      const response = await cancelInvoice(invoiceId);
      message.success(translation.messages.successCancel);
      navigate('/invoices/incomes');
    } catch (error) {
      message.error('Error cancelling invoice. Please try again later.');
    }
  };

  const handleSendEmail = async (invoiceId: any) => {
    try {
      const response = await sendEmail(invoiceId);
      message.success(translation.messages.successSent);
    } catch (error) {
      message.error('Error sending. Please try again later.');
    }
  };

  const handleDownload = async (invoiceId: any) => {
    try {
      const response = await getInvoiceDownload(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName =
        response.headers['x-file-name'] || `invoice_${invoiceId}.pdf`;
      saveAs(blob, fileName);
      message.success(translation.messages.downloadStart);
    } catch (error) {}
  };

  const handlePrint = async (invoiceId: any) => {
    setIsPrinting(true);
    message.loading({ content: translation.pdfPreparing, key: 'printLoading' });

    try {
      const response = await getInvoiceDownload(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);

      message.success({ content: translation.pdfReady, key: 'printLoading' });

      const pdfTab = window.open(pdfUrl, '_blank');

      if (pdfTab) {
        pdfTab.onload = () => {
          setTimeout(() => {
            try {
              pdfTab.print();

              pdfTab.addEventListener('afterprint', () => {
                pdfTab.close();
              });

              setTimeout(() => {
                if (!pdfTab.closed) {
                  pdfTab.close();
                }
              }, 30000);
            } catch {
              pdfTab.close();
            }
          }, 1000);
        };

        pdfTab.blur();
        window.focus();

        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 60000);
      } else {
        message.error('Çap səhifəsi açıla bilmədi. Pop-up blocker-i yoxlayın.');
      }
    } catch (error) {
      message.error('Çap zamanı xəta baş verdi');
    } finally {
      setIsPrinting(false);
    }
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      managerId:
        managers?.find((m) => m.value == invoice?.managerId) ||
        managers?.find((m) => String(m.value) === String(invoice?.managerId)) ||
        null,
      invoiceJobId: jobNumbers?.find((j) => j.value === invoice?.invoiceJobId),
      contractNumber: invoice?.contractNumber || '',
      contractDate: invoice?.contractDate
        ? invoice.contractDate.slice(0, 10)
        : '',
    }));
  }, [invoice, managers, jobNumbers]);

  const handleSelectChange = (name: string, option: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: option,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    setLoading(true);
    event.preventDefault();
    const payload = {
      managerId: formData?.managerId?.value,
      invoiceJobId: formData?.invoiceJobId?.value || null,
      invoiceId: Number(invoiceId),
      contractNumber: formData?.contractNumber || null,
      contractDate: formData?.contractDate || null,
    };

    const method = updateInvoice;
    method(payload)
      .then(() => {
        message.success(`${translation.messages.successUpdated}`);
        queryClient.invalidateQueries({ queryKey: ['invoiceDetails'] });
      })
      .catch((error: InvoiceDetailsHeaderCustomError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleMakeAkt = async (invoiceId: any) => {
    try {
      const response = await makeAkt(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName =
        response.headers['x-file-name'] || `invoice_${invoiceId}.pdf`;
      saveAs(blob, fileName);
      message.success(translation.InvoiceDetails.makeAktSuccess);
    } catch (error: any) {
      const data = error?.response?.data;
      let errorMessage = translation.InvoiceDetails.makeAktError;

      if (typeof data === 'string' && data.trim()) {
        errorMessage = data.trim();
      } else if (data instanceof Blob) {
        const text = await data.text();
        if (text.trim()) {
          errorMessage = text.trim();
        }
      }

      message.error(errorMessage);
    }
  };

  const handleDeletePayment = async (paymentReceiveId: number) => {
    try {
      await deleteInvoicePayment(paymentReceiveId);
      message.success(translation.messages.successDeleted);
      resetPaymentData();
      refetchPaymentDetails();
      refetchInvoiceDetails();
      refetchInvoiceLineDetails();
      queryClient.invalidateQueries([INVOICE_PAYMENT_DETAILS, invoiceId]);
      queryClient.invalidateQueries([INVOICE_DETAILS, invoiceId]);
      queryClient.invalidateQueries([REPORTS_EXPENSES_SUMMARY, partnerId]);
    } catch (error) {
      message.error('Error deleting payment. Please try again later.');
    }
  };

  const handleGoBack = () => {
    if (previousPageUrl === REPORTS_PAGE_URL) {
      navigate(-1);
    } else if (previousPageUrl === INCOMES_PAGE_URL) {
      navigate('/invoices/incomes');
    } else navigate(-1);
  };

  const paymentColumns: ColumnsType<any> = [
    {
      key: 'paymentMethodDesc',
      title: translation.InvoiceDetails.PaymentDetails,
      dataIndex: 'paymentMethodDesc',
      align: 'center',
      width: 200,
    },
    {
      key: 'paymentMethod',
      title: translation.InvoiceDetails.PaymentMethod,
      dataIndex: 'paymentMethod',
      align: 'center',
      width: 200,
    },
    {
      key: 'paymentAmount',
      title: translation.InvoiceDetails.Total,
      dataIndex: 'paymentAmount',
      align: 'center',
      width: 200,
      render: (value: number, record: PaymentData) =>
        `${new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value || 0)} ${record?.currency || ''}`,
    },
    {
      key: 'taxAmount',
      title: translation.InvoiceDetails.Tax,
      dataIndex: 'taxAmount',
      align: 'center',
      width: 200,
      render: (value: number, record: PaymentData) =>
        `${new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value || 0)} ${record?.currency || ''}`,
    },
    {
      key: 'paymentReceivedDate',
      title: translation.InvoiceDetails.PaymentDate,
      dataIndex: 'paymentReceivedDate',
      align: 'center',
      width: 200,
      render: (value: string) => value.slice(0, 10),
    },
    {
      title: translation.action.Action,
      key: 'actions',
      align: 'center',
      width: 200,
      render: (_, record: PaymentData) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDeletePayment(record.paymentReceiveId)}
                  okText={translation.InvoiceDetails.Yes}
                  placement="left"
                  cancelText={translation.InvoiceDetails.No}
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle" style={{ paddingRight: '10px' }}>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDeletePayment(record.paymentReceiveId)}
              okText={translation.InvoiceDetails.Yes}
              cancelText={translation.InvoiceDetails.No}
            >
              <div style={{ cursor: 'pointer' }}>
                <img
                  className="delete"
                  src="assets/delete-icon.svg"
                  alt="Delete"
                />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const itemColumns: ColumnsType<any> = [
    {
      title: translation.InvoiceDetails.ItemName,
      dataIndex: 'description',
      align: 'center',
      key: 'description',
      width: 500,
      render: (text) => (
        <div
          style={{
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            padding: '20px',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: translation.InvoiceDetails.SaleType,
      dataIndex: 'salesTypeDetail',
      key: 'salesTypeDetail',
      width: 200,
      align: 'center',
    },
    {
      title: translation.InvoiceDetails.Price,
      dataIndex: 'price',
      key: 'price',
      width: 200,
      render: (value) =>
        `${new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value || 0)} ${invoice?.currency}`,
      align: 'center',
    },
    {
      title: translation.InvoiceDetails.Quantity,
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      width: 200,
    },
    {
      title: translation.InvoiceDetails.Unit,
      dataIndex: 'measurementUnitDetail',
      key: 'measurementUnitDetail',
      width: 200,
      align: 'center',
    },
    {
      title: translation.InvoiceDetails.Weight,
      dataIndex: 'weight',
      key: 'weight',
      width: 200,
      render: (value) => `${value} kq`,
      align: 'center',
    },
    {
      title: translation.InvoiceDetails.Discount,
      dataIndex: 'discountRate',
      key: 'discountRate',
      width: 200,
      render: (value) => `${value} %`,
      align: 'center',
    },
    {
      title: translation.InvoiceDetails.TaxType,
      dataIndex: 'taxTypeDetail',
      key: 'taxTypeDetail',
      align: 'center',
      width: 200,
    },
  ];

  const showPage = invoiceDetails && managers && jobNumbers;
  const actionBtnBase =
    'flex w-full items-center justify-center gap-[6px] rounded-[6px] border px-[12px] py-[8px] text-[14px] font-medium leading-[16px] transition-all duration-300 md:w-max md:px-[24px]';
  const btnBlue = `${actionBtnBase} 
   bg-blue-50 border-blue-200 text-blue-700 
   hover:bg-blue-100`;
  const btnPurple = `${actionBtnBase} 
   bg-purple-50 border-purple-200 text-purple-700 
   hover:bg-purple-100`;
  const btnGreen = `${actionBtnBase} 
   bg-green-50 border-green-200 text-green-700 
   hover:bg-green-100`;
  const btnOrange = `${actionBtnBase} 
   bg-orange-50 border-orange-200 text-orange-700 
   hover:bg-orange-100`;

  return (
    showPage && (
      <>
        <div className="flex-wrap items-center justify-between gap-[12px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:flex">
          <div className="flex w-full flex-wrap items-center justify-between gap-[10px] md:w-auto md:justify-start md:gap-[24px]">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-[5px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[14px] font-medium text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[20px]"
            >
              <ArrowBackIosNewIcon style={{ width: '14px', height: '14px' }} />
              {translation.InvoiceDetails.Back}
            </button>
            <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
              {invoice.invoiceNumber}
            </p>
            <StatusTag invoiceStatus={invoice?.invoiceStatus} />
          </div>
          <div className="mt-[12px] grid grid-cols-2 flex-wrap items-center gap-[10px] md:mt-[unset] md:flex">
            <button
              onClick={() => handleMakeAkt(invoiceId!)}
              className={btnBlue}
            >
              <FolderAddOutlined className="hidden md:block" />
              <p>{translation.InvoiceDetails.MakeAct}</p>
            </button>
            {accountData?.countryCode &&
              accountData?.countryCode === 'AZ' &&
              accountData.defaultCurrencyId === 'AZN' && (
                <button onClick={openInvoiceDialog} className={btnOrange}>
                  <ProfileOutlined className="hidden md:block" />
                  <p>{translation.electronicInvoice.Header}</p>
                </button>
              )}
            <button onClick={handleOpenModal} className={btnPurple}>
              <CheckOutlined className="hidden md:block" />
              <p>{translation.InvoiceDetails.ReceivePayment}</p>
            </button>

            <button
              onClick={() => handleSendEmail(invoiceId!)}
              className={btnGreen}
            >
              <ArrowRightOutlined className="hidden md:block" />
              <p>{translation.InvoiceDetails.SendToCustomer} </p>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          <div className="col-span-2">
            <Formik
              key={formKey}
              enableReinitialize={true}
              initialValues={{
                customerName: invoice.partnerName,
                countryName: invoice.countryName,
                location: invoice.partnerLocation,
                invoiceNumber: invoice.invoiceNumber,
                managerId:
                  managers?.find(
                    (manager) => manager.value == invoice?.managerId
                  ) ||
                  managers?.find(
                    (manager) =>
                      String(manager.value) === String(invoice?.managerId)
                  ) ||
                  null,
                invoiceDate: invoice.invoiceDate.slice(0, 10),
                dueDate: invoice.dueDate.slice(0, 10),
                invoiceJobId: jobNumbers?.find(
                  (jobId) => jobId.value === invoice?.invoiceJobId
                ),
              }}
              onSubmit={() => {}}
            >
              {({ setFieldValue, handleChange, values }) => (
                <Form>
                  <div className="flex grid-cols-3 flex-wrap items-center gap-[32px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:grid md:py-[24px]">
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.CustomerName}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.partnerName}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.CountryName}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.countryName}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.Location}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.partnerLocation}
                      </p>
                    </div>
                  </div>
                  <div className="flex grid-cols-3 flex-wrap items-center gap-[32px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:grid md:py-[24px]">
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.electronicInvoice.LabelText}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.qaimeNo || translation.subusers.noData}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.electronicInvoice.LabelDateText}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.qaimeDate
                          ? invoice.qaimeDate?.slice(0, 10)
                          : translation.subusers.noData}
                      </p>
                    </div>
                  </div>
                  <div className="flex grid-cols-3 flex-wrap items-center gap-[32px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:grid md:py-[24px]">
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.InvoiceNumber}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.invoiceNumber}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.InvoiceDate}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.invoiceDate?.slice(0, 10)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.DueDate}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {invoice.dueDate.slice(0, 10)}
                      </p>
                    </div>
                  </div>
                  <div className="grid items-end gap-[12px] border-b border-b-[#f5f7f9] px-[16px] py-[24px] md:grid-cols-2 md:gap-[32px] lg:grid-cols-3">
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.JobNumber}
                      </p>
                      <Select
                        onChange={(option: any) => {
                          handleSelectChange('invoiceJobId', option);
                        }}
                        isClearable
                        classNamePrefix="react-select"
                        options={jobNumbers}
                        value={formData?.invoiceJobId}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.Manager}
                      </p>
                      <Select
                        onChange={(option: any) =>
                          handleSelectChange('managerId', option)
                        }
                        classNamePrefix="react-select"
                        options={managers}
                        value={formData?.managerId}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.invoicesAdd.ContractNumber}
                      </p>
                      <Input
                        type="text"
                        name="contractNumber"
                        onChange={handleInputChange}
                        value={formData.contractNumber}
                        maxLength={20}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.invoicesAdd.ContractDate}
                      </p>
                      <Input
                        type="date"
                        name="contractDate"
                        onChange={handleInputChange}
                        value={formData.contractDate}
                      />
                    </div>
                    <FinishSendBtn type="submit" onClick={handleSubmit}>
                      {loading ? (
                        <CircularProgress size={22} />
                      ) : (
                        translation.info.saveButton
                      )}
                    </FinishSendBtn>
                  </div>
                </Form>
              )}
            </Formik>
            <div className="flex flex-col gap-[24px] px-[16px] py-[12px] md:border-b md:border-b-[#f5f7f9]">
              <div className="grid grid-cols-1 items-center gap-[12px] md:grid-cols-3 md:gap-[24px]">
                <button
                  onClick={() => handleDownload(invoiceId!)}
                  className="flex w-full items-center justify-center gap-[6px] rounded-[6px] rounded-[6px] border border-[#CECECE] border-blue-200 bg-blue-50 px-[12px] py-[8px] text-[14px] font-normal leading-[16px] text-black text-blue-700 transition-all duration-300 ease-in-out hover:bg-blue-100 hover:bg-gray-100 md:px-[24px]"
                >
                  <FileAddOutlined />
                  <p>{translation.InvoiceDetails.Download}</p>
                </button>
                <button
                  onClick={
                    isPrinting ? undefined : () => handlePrint(invoiceId!)
                  }
                  className="flex w-full items-center justify-center gap-[6px] rounded-[6px] rounded-[6px] border border-[#CECECE] border-green-200 bg-green-50 px-[12px] py-[8px] text-[14px] font-normal leading-[16px] text-black text-green-700 transition-all duration-300 ease-in-out hover:bg-gray-100 hover:bg-green-100 md:px-[24px]"
                >
                  <Printer />
                  <p>
                    {isPrinting
                      ? 'Hazırlanır...'
                      : translation.InvoiceDetails.Print}
                  </p>
                </button>
                <Popconfirm
                  title={translation.InvoiceDetails.cancelInvoice}
                  onConfirm={() => handleCancelInvoice(invoiceId!)}
                  okText={
                    <span style={{ color: 'black' }}>
                      {translation.InvoiceDetails.Yes}
                    </span>
                  }
                  cancelText={
                    <span style={{ color: 'black' }}>
                      {translation.InvoiceDetails.No}
                    </span>
                  }
                >
                  <button className="flex w-full items-center justify-center gap-[6px] rounded-[6px] rounded-[6px] border border-red-200 border-red-500 bg-red-50 px-[12px] py-[8px] text-[14px] font-normal leading-[16px] text-black text-red-700 transition-all duration-300 ease-in-out hover:bg-red-100 hover:bg-red-100 md:px-[24px]">
                    <CloseCircleOutlined />
                    <p>{translation.InvoiceDetails.CancelInvoice}</p>
                  </button>
                </Popconfirm>
              </div>
            </div>
          </div>
          <InvoiceDetailsInfo invoice={invoice} />
        </div>

        <div className="flex flex-col gap-[24px] px-[16px] pb-[24px] pt-[12px]">
          <div className="flex flex-col gap-[12px]">
            <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
              {translation.InvoiceDetails.ItemList}
            </p>
            <Table
              bordered
              className="custom-bordered"
              scroll={{ x: 'max-content' }}
              dataSource={invoiceLinesDetails?.map((item, i) => ({
                ...item,
                key: i,
              }))}
              columns={itemColumns}
              pagination={false}
              rowClassName={(_, i) =>
                i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
              }
              style={{ tableLayout: 'fixed' }}
            />
          </div>

          <div className="flex flex-col gap-[12px]">
            <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
              {translation.InvoiceDetails.PaymentDetails}
            </p>
            <ScrollContainer ref={tableContainerRef}>
              <Table
                bordered
                className="custom-bordered"
                scroll={{ x: 'max-content' }}
                dataSource={paymentTableData}
                columns={paymentColumns}
                loading={isPaymentLoading && pageIndex === 0}
                pagination={false}
                rowClassName={(_, i) =>
                  i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
                }
              />
              <LoadingIndicator ref={loadingRef}>
                {isLoadingMore && <Spin size="small" />}
                {!hasMore && paymentTableData.length > 0 && (
                  <div style={{ color: '#999', marginTop: '8px' }}>
                    {translation.messages.allData}
                  </div>
                )}
              </LoadingIndicator>
            </ScrollContainer>
          </div>
        </div>

        <PaymentModal
          open={openModal}
          handleClose={handleCloseModal}
          invoiceDetails={invoiceDetails}
          onPaymentAdded={() => {
            resetPaymentData();
            refetchPaymentDetails();
            refetchInvoiceDetails();
            refetchInvoiceLineDetails();
            queryClient.invalidateQueries([INVOICE_PAYMENT_DETAILS, invoiceId]);
            queryClient.invalidateQueries([
              REPORTS_EXPENSES_SUMMARY,
              partnerId,
            ]);
          }}
          title={translation.PaymentReceived.ReceivedPayment}
          isCost={false}
        />

        <AddNewInvoiceForm
          open={invoiceDialogState.isOpen}
          handleClose={closeInvoiceDialog}
        />
      </>
    )
  );
};

const Select = styled(ReactSelect)`
  & .react-select__value-container {
    padding: 8.7px 16px;
  }
`;

const ScrollContainer = styled.div`
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  position: relative;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
`;

const FinishSendBtn = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 40px;
  background: #6dcd90;
  border-radius: 6px;
  font-weight: 500;
  color: #fff;
`;

const Input = styled.input`
  background: #ffffff;
  border: 1px solid rgba(41, 45, 50, 0.25);
  width: 100%;
  height: 48px;
  font-family: 'Mark Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  padding: 6px 15px;
  border-radius: 6px;
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
    padding: 5px 14px;
  }
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

export default InvoiceDetailsHeader;
